import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  Grid,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const AccessProvisioningManagement = () => {
  const { tokenManager, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [accessItems, setAccessItems] = useState({
    osm: false,
    osmSaas: false,
    diveInPanel: false,
    gmos: false,
    panelRecruitment: false,
    nonProjectExpense: false,
    itSupportTicketing: false,
    erp: false,
    taskAssigner: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [isEmployeeView, setIsEmployeeView] = useState(false);

  // Check if current user is an employee (not admin/itsupport)
  const isCurrentUserEmployee = () => {
    if (!currentUser) return false;
    const roles = currentUser.roles || [];
    const isAdmin = roles.includes('admin');
    const isITSupport = roles.includes('itsupport');
    const isEmployee = roles.includes('employee');
    // Employee view if they have employee role but not admin/itsupport
    return isEmployee && !isAdmin && !isITSupport;
  };

  // Complete access items configuration
  const allAccessConfig = [
    // Online-specific items
    {
      key: 'osm',
      label: 'OSM',
      description: 'Operations and Service Management',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'online',
    },
    {
      key: 'osmSaas',
      label: 'OSM SAAS',
      description: 'OSM Software as a Service',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'online',
    },
    {
      key: 'diveInPanel',
      label: 'Divein Panel',
      description: 'Dive In Panel System',
      icon: <SecurityIcon />,
      color: 'secondary',
      role: 'online',
    },
    {
      key: 'gmos',
      label: 'GMOS',
      description: 'Marketplace for Online Sampling',
      icon: <SecurityIcon />,
      color: 'secondary',
      role: 'online',
    },
    {
      key: 'panelRecruitment',
      label: 'Panel Recruitment',
      description: 'Panel Recruitment System',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'online',
    },
    {
      key: 'nonProjectExpense',
      label: 'Non Project Expense',
      description: 'Non Project Expense Management',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'both',
    },
    {
      key: 'itSupportTicketing',
      label: 'IT Support (Ticket Raising)',
      description: 'Ticket Raising & Support System',
      icon: <PersonIcon />,
      color: 'warning',
      role: 'both',
    },
    // Offline-specific items
    {
      key: 'erp',
      label: 'ERP System Access',
      description: 'Enterprise Resource Planning',
      icon: <BusinessIcon />,
      color: 'primary',
      role: 'offline',
    },
    {
      key: 'taskAssigner',
      label: 'Task Assigner',
      description: 'Task Assignment and Management',
      icon: <InventoryIcon />,
      color: 'success',
      role: 'offline',
    },
  ];

  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Auto-select current user if they are an employee
  useEffect(() => {
    if (currentUser && isCurrentUserEmployee() && users.length > 0) {
      setSelectedUser(currentUser._id);
      setIsEmployeeView(true);
    }
  }, [currentUser, users]);

  // Get applicable access config based on user's role
  const getApplicableAccessConfig = () => {
    const user = users.find(u => u._id === selectedUser);
    if (!user) return [];

    if (user.roles.includes('online')) {
      return allAccessConfig.filter(item => item.role === 'online' || item.role === 'both');
    } else if (user.roles.includes('offline')) {
      return allAccessConfig.filter(item => item.role === 'offline' || item.role === 'both');
    }
    return [];
  };

  const accessConfig = getApplicableAccessConfig();

  // Debug: Log when users state changes
  // useEffect(() => {
  //   console.log('Users state changed:', users);
  //   console.log('Users length:', users.length);
  // }, [users]);

  // Fetch users from backend
  const fetchUsers = async () => {
    setUserLoading(true);
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        // console.log('API Response:', result);
        // console.log('Response type:', typeof result);
        // console.log('Is array:', Array.isArray(result));
        
        // Backend returns users directly, not wrapped in success/data structure
        if (Array.isArray(result)) {
          // console.log('Setting users array:', result);
          setUsers(result);
        } else if (result.success && result.data) {
          // console.log('Setting users from success.data:', result.data);
          setUsers(result.data);
        }
      } else {
        console.error('Failed to fetch users');
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setUserLoading(false);
    }
  };

  // Fetch access provisioning data when user is selected
  useEffect(() => {
    if (selectedUser) {
      fetchAccessProvisioning();
    } else {
      setAccessItems({
        osm: false,
        osmSaas: false,
        diveInPanel: false,
        gmos: false,
        panelRecruitment: false,
        nonProjectExpense: false,
        itSupportTicketing: false,
        erp: false,
        taskAssigner: false,
      });
    }
  }, [selectedUser]);

  // Fetch access provisioning data from backend
  const fetchAccessProvisioning = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/access-provisioning/${selectedUser}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAccessItems({
            osm: result.data.osm || false,
            osmSaas: result.data.osmSaas || false,
            diveInPanel: result.data.diveInPanel || false,
            gmos: result.data.gmos || false,
            panelRecruitment: result.data.panelRecruitment || false,
            nonProjectExpense: result.data.nonProjectExpense || false,
            itSupportTicketing: result.data.itSupportTicketing || false,
            erp: result.data.erp || false,
            taskAssigner: result.data.taskAssigner || false,
          });
        }
      } else {
        console.error('Failed to fetch access provisioning data');
        // Set default values if no data exists
        setAccessItems({
          osm: false,
          osmSaas: false,
          diveInPanel: false,
          gmos: false,
          panelRecruitment: false,
          nonProjectExpense: false,
          itSupportTicketing: false,
          erp: false,
          taskAssigner: false,
        });
      }
    } catch (error) {
      console.error('Error fetching access provisioning:', error);
      // Set default values on error
      setAccessItems({
        osm: false,
        osmSaas: false,
        diveInPanel: false,
        gmos: false,
        panelRecruitment: false,
        nonProjectExpense: false,
        itSupportTicketing: false,
        erp: false,
        taskAssigner: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox changes
  const handleAccessChange = (key) => (event) => {
    const newAccessItems = {
      ...accessItems,
      [key]: event.target.checked,
    };
    setAccessItems(newAccessItems);

    // Save to backend
    saveAccessProvisioning(newAccessItems);
  };

  // Save access provisioning to backend
  const saveAccessProvisioning = async (accessData) => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/access-provisioning`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
        body: JSON.stringify({
          employeeSelectId: selectedUser,
          osm: accessData.osm,
          osmSaas: accessData.osmSaas,
          diveInPanel: accessData.diveInPanel,
          gmos: accessData.gmos,
          panelRecruitment: accessData.panelRecruitment,
          nonProjectExpense: accessData.nonProjectExpense,
          itSupportTicketing: accessData.itSupportTicketing,
          erp: accessData.erp,
          taskAssigner: accessData.taskAssigner,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Access provisioning updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update access provisioning');
      }
    } catch (error) {
      console.error('Error saving access provisioning:', error);
      toast.error('Failed to save access provisioning');
    } finally {
      setSaving(false);
    }
  };

  // Get selected user details
  const getSelectedUserDetails = () => {
    return users.find(user => user._id === selectedUser);
  };

  const selectedUserDetails = getSelectedUserDetails();

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Container maxWidth={false}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: '30px',lineHeight:1.5 ,letterSpacing:1 }}>
          <SecurityIcon color="primary" />
          Access Provisioning Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage system access provisioning for employees
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {users.length} employees available for access provisioning
        </Typography>
      </Box>

      {/* User Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {isEmployeeView ? 'Your Access Provisioning' : 'Select Employee'}
        </Typography>
        {isEmployeeView && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You are viewing your own access provisioning. Below are all the systems you have access to or can request access for.
          </Alert>
        )}
        <Autocomplete
          sx={{ maxWidth: 400 }}
          options={users || []}
          value={users.find(u => u._id === selectedUser) || null}
          onChange={(event, newValue) => {
            setSelectedUser(newValue ? newValue._id : '');
          }}
          disabled={isEmployeeView || userLoading}
          getOptionLabel={(option) => `${option.firstName || 'N/A'} ${option.lastName || 'N/A'} - ${option.email || 'N/A'}`}
          loading={userLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Choose Employee"
              placeholder="Search employee by name or email..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {userLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, user) => (
            <Box component="li" {...props} key={user._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <PersonIcon fontSize="small" sx={{ flexShrink: 0 }} />
                <Typography variant="body1">
                  {user.firstName || 'N/A'} {user.lastName || 'N/A'} - {user.email || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
          filterOptions={(options, { inputValue }) => {
            return options.filter((option) => {
              const searchTerm = inputValue.toLowerCase();
              const firstName = (option.firstName || '').toLowerCase();
              const lastName = (option.lastName || '').toLowerCase();
              const email = (option.email || '').toLowerCase();
              const fullName = `${firstName} ${lastName}`.trim();
              return fullName.includes(searchTerm) || email.includes(searchTerm);
            });
          }}
          noOptionsText={userLoading ? 'Loading users...' : 'No employees found'}
        />
        
        {/* Debug info
        <Box sx={{ mt: 2, p: 2, bgcolor: 'yellow.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Debug Info:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users loaded: {users.length}<br/>
            Selected user: {selectedUser}<br/>
            User loading: {userLoading ? 'Yes' : 'No'}
          </Typography>
        </Box> */}
        
                 {selectedUserDetails && (
           <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
             <Typography variant="subtitle2" gutterBottom>
               Selected Employee Details:
             </Typography>
             <Typography variant="body2" color="text.secondary">
               <strong>Name:</strong> {selectedUserDetails.firstName} {selectedUserDetails.lastName}<br/>
               <strong>Email:</strong> {selectedUserDetails.email}<br/>
               <strong>Department:</strong> {selectedUserDetails.department || 'N/A'}<br/>
               <strong>Job Title:</strong> {selectedUserDetails.jobTitle || 'N/A'}<br/>
               <strong>Roles:</strong> {selectedUserDetails.roles && selectedUserDetails.roles.length > 0 
                 ? selectedUserDetails.roles.map(role => 
                     role === 'itsupport' ? 'IT Support' : 
                     role === 'admin' ? 'Admin' :
                     role === 'online' ? 'Online' :
                     role === 'offline' ? 'Offline' :
                     role === 'employee' ? 'Employee' : role
                   ).join(', ')
                 : 'N/A'}
             </Typography>
           </Box>
         )}
      </Paper>

      {/* Access Provisioning Section */}
      {selectedUser && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            {isEmployeeView ? 'Your System Access' : 'Access Provisioning Setup'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isEmployeeView 
              ? 'Below are all the systems you can access. Check the boxes to manage your access requests.' 
              : `Configure system access for ${selectedUserDetails?.firstName || 'the employee'}. Check the boxes to grant access to different systems.`}
          </Typography>

          {/* Loading and Saving Indicators */}
          {loading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Loading access provisioning data...
            </Alert>
          )}
          
          {saving && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Saving changes...
            </Alert>
          )}

          {/* Access Items Grid */}
          {accessConfig.length > 0 ? (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {accessConfig.map((item) => (
                <Grid item xs={12} md={4} key={item.key}>
                  <Card 
                    elevation={accessItems[item.key] ? 3 : 1}
                    sx={{ 
                      border: accessItems[item.key] ? `2px solid ${item.color}` : '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      bgcolor: accessItems[item.key] ? `${item.color}.50` : 'white',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box sx={{ color: `${item.color}.main` }}>
                          {item.icon}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.label}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {item.description}
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={accessItems[item.key]}
                            onChange={handleAccessChange(item.key)}
                            color={item.color}
                            size="large"
                            disabled={saving}
                          />
                        }
                        label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {accessItems[item.key] ? 'Access Granted' : 'Grant Access'}
                          </Typography>
                          {accessItems[item.key] && (
                            <Chip 
                              label="✓ Provisioned" 
                              size="small" 
                              color={item.color}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
            </Grid>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                No access items available
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                This employee is not assigned as either "Online" or "Offline". 
                Please assign one of these roles to the employee first in the User Management section.
              </Typography>
            </Alert>
          )}

        </Paper>
      )}

      {/* Instructions */}
      {!selectedUser && (
        <Paper sx={{ p: 3, bgcolor: 'blue.50' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Instructions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1. Select an employee from the dropdown above<br/>
            2. Configure their system access by checking the appropriate boxes<br/>
            3. Changes are automatically saved when you make selections<br/>
            4. Monitor the progress to ensure all required access is granted
          </Typography>
        </Paper>
      )}
      </Container>
    </Box>
  );
};

export default AccessProvisioningManagement;
